import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SIDENAV_DATA } from '@shared/services/sidenav/sidenav.data';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { UIModule } from '@shared/modules/ui/ui.module';
import { SharedModule } from '@shared/shared.module';
import { FormsV2Module } from '@shared/modules/forms-v2/forms-v2.module';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { AttachmentManagerListV2Component } from '@shared/modules/attachment-manage/components/attachment-manager-list-v2/attachment-manager-list-v2.component';
import { AttachmentsManageModule } from '@shared/modules/attachment-manage/attachments-manage.module';
import { MailingQuillFormats } from '@modules/mailing-templates/shared/modules/mailing-sidenav/components/mailing-sidenav/data/mailing-quill-formats.data';
import { MailingQuillModules } from '@modules/mailing-templates/shared/modules/mailing-sidenav/components/mailing-sidenav/data/mailing-quill-modules.data';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ObjectTaskType, TaskType } from '@shared/enums/task-type.enum';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { Task } from '@shared/models/task.model';
import { of } from 'rxjs';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { Config } from '@shared/configs/config';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { Regex } from '@shared/configs/regex';
import { AssignedContactList } from '@shared/models/assigned-contact.model';
import { NgxMatTimepickerComponent, NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import moment from 'moment';

interface ProcessMessageSidenavData {
  mode?: 'create' | 'send' | 'edit';
  projectId: number;
  stage: ProjectStage;
  sortOrder: number;
  parentId: number | null;
  groupId?: number | null;
  mapperTaskId?: number | null;
  task?: Task | null;
  taskType?: TaskType | string | null;
}

interface EmailMapperToken {
  key: string;
  label: string;
  value: string;
}

@Component({
  selector: 'project-stage-process-message-sidenav',
  standalone: true,
  templateUrl: './process-message-task-sidenav.component.html',
  styleUrls: ['./process-message-task-sidenav.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NgSelectModule,
    AngularSvgIconModule,
    UIModule,
    FormsV2Module,
    SharedModule,
    AttachmentsManageModule,
    NgxMatTimepickerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessMessageTaskSidenavComponent implements OnInit {
  private readonly sideService = inject(SidenavService);
  private readonly fb = inject(FormBuilder);
  private readonly taskService = inject(TaskService);
  private readonly listTaskService = inject(ListTaskService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('editor') editor: any;
  @ViewChild('attachmentsList') attachmentsList?: AttachmentManagerListV2Component;
  @ViewChild('subjectWrapper', { read: ElementRef }) subjectWrapper?: ElementRef<HTMLElement>;

  protected readonly data = (this.sideData?.data || {}) as ProcessMessageSidenavData;
  protected readonly mode = signal<'create' | 'send' | 'edit'>('create');
  protected readonly taskDetails = signal<Task | null>(null);
  protected showBccField = signal<boolean>(false);
  protected showCcField = signal<boolean>(false);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly formats = MailingQuillFormats;
  protected readonly modules = MailingQuillModules();
  protected variableTokens: EmailMapperToken[] = [];
  protected initialEmails: any[] = [];
  protected assignedToInitialValue: any[] = [];
  private readonly assignedContactList = new AssignedContactList();
  protected today = new Date();
  private lastSavedAssignmentSignature = '';
  private readonly closingSidenav = signal(false);

  protected timepicker = viewChild<NgxMatTimepickerComponent>('picker');

  protected readonly attachmentsConfig: TextAttachmentsConfig = {
    fileAttachments: true,
    photoAttachments: true,
    videoAttachments: true,
    maxAttachmentSize: 50
  };

  protected saving = false;
  protected loadingVariables = false;
  protected activeField: 'subject' | 'content' = 'content';
  protected subjectCaretStart = 0;
  protected subjectCaretEnd = 0;
  protected quillCaretIndex: number | null = null;

  protected readonly form = this.fb.group({
    templateName: ['', Validators.required],
    projectId: [null as number | null],
    projectName: [''],
    to: [''],
    bcc: [[] as string[]],
    cc: [[] as string[]],
    subject: ['', Validators.required],
    content: ['', Validators.required],
    assignedToIds: [[] as number[]],
    assignedEmails: [[] as string[]],
    sendDate: [null],
    sendTime: [null]
  });

  protected get contactsUrl(): string {
    return `${Config.API}/contact/search?emailsOnly=1`;
  }

  protected get attachmentUploadConfig(): { id: number; objectType: string } | null {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    return taskId ? { id: taskId, objectType: 'task' } : null;
  }

  protected get taskAttachments(): any[] {
    const task = this.taskDetails() || this.data?.task;
    return task?.originalAndChangeAttachments || task?.attachments || [];
  }

  constructor(@Inject(SIDENAV_DATA) private readonly sideData: SIDENAV_DATA) {}

  ngOnInit(): void {
    const initialMode = this.data?.mode;
    this.mode.set(initialMode === 'send' || initialMode === 'edit' ? initialMode : 'create');
    this.initFormForMode();
    this.initAssignedToAutosave();
    this.loadExistingTaskDetails();
    this.loadEmailMappers();
  }

  protected close(): void {
    this.closingSidenav.set(true);
    this.sideService.close();
  }

  protected save(): void {
    if (this.isEditMode()) {
      this.saveEdit();
      return;
    }

    if (this.isSendMode()) {
      this.sendMail();
      return;
    }

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.invalid || this.saving || !this.data?.projectId || !this.data?.stage) {
      this.cdr.markForCheck();
      return;
    }

    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const normalizedAssigned = Array.isArray(assignedRaw) ? assignedRaw : [];
    const assignedToIds = normalizedAssigned
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const assignedEmails = normalizedAssigned
      .map((value) => String(value))
      .filter((value) => value.includes('@'));

    const subjectValue = this.getMessageSubjectValue();
    const contentValue = this.getMessageContentValue();

    const selectedProjectId = Number(this.form.get('projectId')?.value || this.data.projectId || 0);
    if (!selectedProjectId) {
      this.cdr.markForCheck();
      return;
    }

    const payload = {
      id: selectedProjectId,
      projectId: selectedProjectId,
      groupId: this.data.groupId ?? null,
      objectType: ObjectTaskType.PROJECT,
      description: subjectValue,
      type: this.getProcessMessageTaskType(),
      content: contentValue,
      emailTitle: subjectValue,
      emailContent: contentValue,
      quillContent: null,
      price: 0,
      isPriceEstimated: false,
      discount: 0,
      additionalTime: 0,
      isAdditionalTimeEstimated: false,
      hiddenForClient: true,
      isHighPriority: false,
      internalNote: (this.form.get('templateName')?.value || '').trim(),
      tags: [],
      assignedToIds: Array.isArray(assignedToIds) ? assignedToIds : [],
      assignedEmails: Array.isArray(assignedEmails) ? assignedEmails : [],
      emailTemplateId: null,
      attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
      attachments: [],
      dataChanges: 1,
      status: TaskStatus.CREATED,
      stage: this.data.stage,
      sortOrder: this.data.sortOrder ?? 1,
      parentId: this.data.parentId ?? null
    };

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .createTask(payload)
      .pipe(
        tap((task: Task) => {
          this.listTaskService.emitter.emit({ type: ListTaskEventType.ADD_TASK, data: task });
          this.snackBarService.success(this.translateService.instant('Tasks.messageCreated'));
          this.sideService.close();
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected get sidenavTitle(): string {
    if (this.isSendMode()) {
      return (
        (this.data?.task?.internalNote || this.data?.task?.description || '').trim() ||
        this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendTitle')
      );
    }
    if (this.isEditMode()) {
      return this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.editTitle');
    }
    return this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.title');
  }

  protected isSendMode(): boolean {
    return this.mode() === 'send' && !!this.data?.task?.id;
  }

  protected isEditMode(): boolean {
    return this.mode() === 'edit' && !!this.data?.task?.id;
  }

  protected isCreateMode(): boolean {
    return this.mode() === 'create' || !this.data?.task?.id;
  }

  protected canShowHeaderActions(): boolean {
    return !!this.data?.task?.id;
  }

  protected switchToEditMode(): void {
    if (!this.data?.task?.id || this.mode() === 'edit') return;
    this.mode.set('edit');
    this.initFormForMode();
    this.loadEmailMappers();
    this.cdr.markForCheck();
  }

  protected deleteTask(): void {
    const removeCtrl = new RemoveModalController();
    removeCtrl
      .remove(() => this.deleteTaskConfirmed())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private deleteTaskConfirmed(): void {
    const taskId = Number(this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .deleteTask(taskId)
      .pipe(
        tap(() => {
          this.snackBarService.success(this.translateService.instant('Tasks.messageDeleted'));
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.REMOVE_TASK,
            data: { task: this.data?.task, closeSidenav: true, _isActivityTemplate: true }
          });
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorDelete'));
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onSubjectFocus(event: FocusEvent): void {
    this.activeField = 'subject';
    this.updateSubjectCaret(event.target as HTMLInputElement | null);
  }

  protected onSubjectCaretChanged(event: Event): void {
    this.updateSubjectCaret(event.target as HTMLInputElement | null);
  }

  protected onEditorFocused(): void {
    this.activeField = 'content';
  }

  protected onEditorSelectionChanged(event: { range?: { index: number } | null }): void {
    if (event?.range && typeof event.range.index === 'number') {
      this.activeField = 'content';
      this.quillCaretIndex = event.range.index;
    }
  }

  protected preventVariableBlur(event: MouseEvent): void {
    event.preventDefault();
  }

  protected addVariable(token: EmailMapperToken): void {
    const variable = token?.value || token?.label || '';
    if (!variable) return;

    if (this.activeField === 'subject') {
      this.insertVariableIntoSubject(variable);
      return;
    }

    const quill = this.editor?.quillEditor;
    if (!quill) {
      const current = this.form.get('content')?.value || '';
      this.form.get('content')?.setValue(`${current} ${variable}`.trim());
      return;
    }

    const selection = quill.getSelection();
    const fallbackIndex = typeof this.quillCaretIndex === 'number' ? this.quillCaretIndex : quill.getLength();
    const index = selection ? selection.index : fallbackIndex;
    quill.insertText(index, variable, 'user');
    const nextCaret = index + variable.length;
    this.quillCaretIndex = nextCaret;

    const focusAndSetCursor = () => {
      quill.focus();
      quill.setSelection(nextCaret, 0, 'silent');
    };

    focusAndSetCursor();
    requestAnimationFrame(focusAndSetCursor);
    setTimeout(focusAndSetCursor, 0);
    setTimeout(focusAndSetCursor, 16);
  }

  private sendMail(): void {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    const to = String(this.form.get('to')?.value || '').trim();
    if (!to) {
      this.form.get('to')?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const bcc = this.parseEmails(this.form.get('bcc')?.value);
    const cc = this.parseEmails(this.form.get('cc')?.value);
    const subject = this.getMessageSubjectValue();
    const content = this.getMessageContentValue();
    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const normalizedAssigned = Array.isArray(assignedRaw) ? assignedRaw : [];
    const assignedToIds = normalizedAssigned
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const assignedEmails = normalizedAssigned
      .map((value) => String(value))
      .filter((value) => value.includes('@'));

    const sendDate = this.form.get('sendDate')?.value;
    const sendTime = this.form.get('sendTime')?.value;
    let scheduledAt: string | null = null;

    if (sendDate && sendTime) {
      const [hours, minutes] = String(sendTime).split(':').map(Number);
      const m = moment(sendDate).set({ hour: hours, minute: minutes, second: 0 });
      scheduledAt = m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : null;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .editPatchTask(
        {
          description: subject,
          content,
          emailTitle: subject,
          emailContent: content,
          assignedToIds,
          assignedEmails,
          attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
          attachments: []
        },
        taskId
      )
      .pipe(
        switchMap(() => this.taskService.sendTaskMail(taskId, { to, bcc, cc, scheduledAt })),
        tap(() => {
          const currentTask = this.data.task as Task;
          if (currentTask) {
            currentTask.description = subject;
            currentTask.content = content;
            currentTask.assignedEmails = assignedEmails;
            this.listTaskService.emitter.emit({
              type: ListTaskEventType.UPDATE_TASK,
              data: { task: currentTask, _isActivityTemplate: true }
            });
          }
          this.snackBarService.success(
            this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendSuccess')
          );
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(
            this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendError')
          );
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initAssignedToAutosave(): void {
    const assignedControl = this.form.get('assignedToIds');
    if (!assignedControl) return;

    assignedControl.valueChanges
      .pipe(
        filter(() => this.isSendMode()),
        filter(() => !this.closingSidenav()),
        filter(() => !!Number(this.taskDetails()?.id || this.data?.task?.id || 0)),
        map((assignedValue) => {
          const normalizedAssigned = Array.isArray(assignedValue) ? assignedValue : [];
          const assignedToIds = normalizedAssigned
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0);
          const assignedEmails = normalizedAssigned
            .map((value) => String(value))
            .filter((value) => value.includes('@'));

          return {
            assignedToIds,
            assignedEmails,
            signature: this.buildAssignmentSignature(assignedToIds, assignedEmails)
          };
        }),
        distinctUntilChanged((prev, curr) => prev.signature === curr.signature),
        filter(({ signature }) => signature !== this.lastSavedAssignmentSignature),
        switchMap(({ assignedToIds, assignedEmails, signature }) => {
          const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);

          return this.taskService.editPatchTask({ assignedToIds, assignedEmails }, taskId).pipe(
            tap((updatedTask: Task) => {
              this.lastSavedAssignmentSignature = signature;
              const currentTask = this.data.task as Task;
              if (currentTask) {
                currentTask.assignedEmails = assignedEmails;
              }

              this.listTaskService.emitter.emit({
                type: ListTaskEventType.UPDATE_TASK,
                data: { task: updatedTask || currentTask, _isActivityTemplate: true }
              });
            }),
            catchError(() => {
              this.snackBarService.error(this.translateService.instant('Tasks.errorTaskUpdate'));
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private saveEdit(): void {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.invalid) {
      this.cdr.markForCheck();
      return;
    }

    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const normalizedAssigned = Array.isArray(assignedRaw) ? assignedRaw : [];
    const assignedToIds = normalizedAssigned
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const assignedEmails = normalizedAssigned
      .map((value) => String(value))
      .filter((value) => value.includes('@'));

    const subjectValue = this.getMessageSubjectValue();
    const contentValue = this.getMessageContentValue();

    const patchPayload = {
      projectId: Number(this.form.get('projectId')?.value || this.data.projectId || 0) || undefined,
      description: (this.form.get('templateName')?.value || '').trim(),
      content: contentValue,
      emailTitle: subjectValue,
      emailContent: contentValue,
      internalNote: subjectValue,
      assignedToIds,
      assignedEmails,
      attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
      attachments: [],
      status: (this.taskDetails()?.status || this.data?.task?.status || TaskStatus.CREATED) as TaskStatus
    };

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .editPatchTask(patchPayload, taskId)
      .pipe(
        tap((updatedTask: Task) => {
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_TASK,
            data: { task: updatedTask || this.data?.task, _isActivityTemplate: true }
          });
          this.snackBarService.success(this.translateService.instant('Tasks.messageChanged'));
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorTaskUpdate'));
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadEmailMappers(): void {
    if (this.isSendMode()) {
      this.variableTokens = [];
      return;
    }

    const mapperTaskId = Number(
      this.taskDetails()?.id || this.data?.mapperTaskId || this.data?.task?.id || 0
    );
    if (!mapperTaskId) {
      this.variableTokens = [];
      return;
    }

    this.loadingVariables = true;
    this.cdr.markForCheck();

    this.taskService
      .getTaskEmailMappers(mapperTaskId)
      .pipe(
        tap((response) => {
          this.variableTokens = this.normalizeEmailMappers(response);
        }),
        finalize(() => {
          this.loadingVariables = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private normalizeEmailMappers(response: any): EmailMapperToken[] {
    const source = response?.records || response?.mappers || response?.data || response || [];

    if (Array.isArray(source)) {
      return source
        .map((item) => this.normalizeToken(item))
        .filter((item): item is EmailMapperToken => !!item?.key);
    }

    if (source && typeof source === 'object') {
      return Object.entries(source)
        .map(([key, value]) => {
          if (typeof value === 'string') {
            return { key, label: key, value };
          }

          if (value && typeof value === 'object') {
            const normalized = this.normalizeToken({ key, ...(value as object) });
            if (normalized) return normalized;
          }

          return { key, label: key, value: key };
        })
        .filter((item): item is EmailMapperToken => !!item?.key);
    }

    return [];
  }

  private initFormForMode(): void {
    if (this.isCreateMode()) {
      return;
    }

    const task = this.taskDetails();
    if (!task) {
      return;
    }

    const to = String(task?.assignedEmails?.[0] || '');
    const projectData: any = task?.project || {};
    const basicData: any = projectData?.basicDataBox || {};
    const projectName = [
      projectData?.name,
      projectData?.clientData?.name,
      basicData?.address || basicData?.addressStreet
    ]
      .filter((item) => !!item)
      .join(' | ');

    const taskAny: any = task || {};
    const emailTitle = String(taskAny?.emailTitle || task?.description || '');
    const emailContent = String(taskAny?.emailContent || task?.content || '');
    const description = String(task?.description || '');
    const internalNote = String(task?.internalNote || '');
    const isEdit = this.isEditMode();
    const assignedUserInitial = Array.isArray(task?.assignedUsers)
      ? task.assignedUsers.map((user: any) => this.assignedContactList.createTagFromUserPerson(user))
      : [];
    const assignedEmailInitial = Array.isArray(task?.assignedEmails)
      ? task.assignedEmails.map((email: string) => this.assignedContactList.createCustomTag(email))
      : [];
    this.assignedToInitialValue = [...assignedUserInitial, ...assignedEmailInitial];
    const assignedUserValues = assignedUserInitial.map((item: any) => item?.id).filter(Boolean);
    const assignedEmailValues = assignedEmailInitial.map((item: any) => item?.id).filter(Boolean);

    this.form.patchValue(
      {
        templateName: isEdit ? description : internalNote,
        projectId: task?.project?.id || this.data?.projectId || null,
        projectName,
        to,
        bcc: [],
        subject: emailTitle || (isEdit ? internalNote : description),
        content: emailContent,
        assignedToIds: [...assignedUserValues, ...assignedEmailValues],
        assignedEmails: Array.isArray(task?.assignedEmails) ? task.assignedEmails : []
      },
      { emitEvent: false }
    );

    this.lastSavedAssignmentSignature = this.buildAssignmentSignature(
      [...assignedUserValues],
      [...assignedEmailValues]
    );

    if (this.isSendMode()) {
      const firstAssigned = (task as any)?.assignedTo?.[0] || (task as any)?.assignedUsers?.[0] || null;
      const recipientLabel =
        firstAssigned?.userPerson?.previewName || firstAssigned?.previewName || firstAssigned?.name || to;
      this.initialEmails = to ? [{ id: to, email: to, fullName: recipientLabel, name: recipientLabel }] : [];
    }
  }

  private loadExistingTaskDetails(): void {
    const taskId = Number(this.data?.task?.id || 0);
    if (!taskId) return;

    const request$ = this.taskService.getTask(taskId);
    if (!request$) return;

    request$
      .pipe(
        tap((task: Task) => {
          this.taskDetails.set(task);
          this.data.task = task;
          this.initFormForMode();
          this.loadEmailMappers();
          this.cdr.markForCheck();
        }),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getProcessMessageTaskType(): TaskType | string {
    const type = String(this.taskDetails()?.type || this.data?.task?.type || this.data?.taskType || '').trim();
    return this.isMessageLikeTaskType(type) ? type : TaskType.TYPE_MESSAGE;
  }

  private isMessageLikeTaskType(type: string | null | undefined): boolean {
    return type === TaskType.TYPE_MESSAGE || type === TaskType.TYPE_FOLLOW_UP;
  }

  private parseEmails(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object')
            return String((item as any).email || (item as any).id || '').trim();
          return '';
        })
        .map((email) => email.trim())
        .filter((email) => !!email);
    }

    const raw = String(value || '').trim();
    if (!raw) return [];
    return raw
      .split(/[;,]/g)
      .map((item) => item.trim())
      .filter((item) => !!item);
  }

  private buildAssignmentSignature(assignedToIds: number[], assignedEmails: string[]): string {
    return JSON.stringify({
      assignedToIds: [...assignedToIds].sort((a, b) => a - b),
      assignedEmails: [...assignedEmails].sort((a, b) => a.localeCompare(b))
    });
  }

  private getMessageSubjectValue(): string {
    const input = this.resolveSubjectInput();
    const raw = input?.value ?? this.form.get('subject')?.value ?? '';
    return String(raw).trim();
  }

  private getMessageContentValue(): string {
    const quillHtml = this.editor?.quillEditor?.root?.innerHTML;
    const formValue = this.form.get('content')?.value;
    return String(quillHtml ?? formValue ?? '');
  }

  private normalizeToken(item: any): EmailMapperToken | null {
    if (!item) return null;

    if (typeof item === 'string') {
      return { key: item, label: item, value: item };
    }

    const key = item.key || item.placeholder || item.token || item.variable || item.mapper || item.value;
    const value =
      item.value ||
      item.mapper ||
      item.token ||
      item.variable ||
      item.placeholder ||
      item.mapValue ||
      item.key;
    const label = item.label || item.name || item.title || key || value;

    if (!key) return null;
    return { key: String(key), label: String(label), value: String(value || key) };
  }

  private updateSubjectCaret(input: HTMLInputElement | null): void {
    const resolvedInput = this.resolveSubjectInput(input);
    if (!resolvedInput) return;
    this.subjectCaretStart = resolvedInput.selectionStart ?? resolvedInput.value.length ?? 0;
    this.subjectCaretEnd = resolvedInput.selectionEnd ?? this.subjectCaretStart;
  }

  private insertVariableIntoSubject(variable: string): void {
    const subjectCtrl = this.form.get('subject');
    if (!subjectCtrl) return;

    const currentValue = String(subjectCtrl.value || '');
    const start = Math.max(0, this.subjectCaretStart);
    const end = Math.max(start, this.subjectCaretEnd);
    const nextValue = `${currentValue.slice(0, start)}${variable}${currentValue.slice(end)}`;
    const nextCaret = start + variable.length;

    subjectCtrl.setValue(nextValue);
    this.subjectCaretStart = nextCaret;
    this.subjectCaretEnd = nextCaret;

    const input = this.resolveSubjectInput();
    if (input) {
      const focusAndSetCursor = () => {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      };
      focusAndSetCursor();
      requestAnimationFrame(focusAndSetCursor);
      setTimeout(focusAndSetCursor, 0);
      setTimeout(focusAndSetCursor, 16);
    }
  }

  private resolveSubjectInput(input?: HTMLInputElement | null): HTMLInputElement | null {
    if (input && typeof input.selectionStart === 'number') return input;
    const candidate = this.subjectWrapper?.nativeElement?.querySelector('input') || null;
    return candidate instanceof HTMLInputElement ? candidate : null;
  }

  protected validateEmail(value: string): any {
    if (!value?.length) return null;
    if (!Regex.email.test(value.toString())) {
      this.snackBarService.error(this.translateService.instant('FormErrors.email'));
      return null;
    }
    return this.createEmailTag(value);
  }

  protected validateBccEmail(value: string): string | null {
    if (!value?.length) return null;
    const email = value.toString().trim();
    if (!Regex.email.test(email)) {
      this.snackBarService.error(this.translateService.instant('FormErrors.email'));
      return null;
    }
    return email;
  }

  protected successOnLoadContacts({ records }: { records: Contact[] }): Contact[] {
    return [...records.map((contact) => new Contact(contact))];
  }

  private createEmailTag(name: string): any {
    return { id: name, name, newItem: true, fullName: name, email: name };
  }

  protected toggleBccInput(): void {
    this.showBccField.set(!this.showBccField());
  }

  protected toggleCcInput(): void {
    this.showCcField.set(!this.showCcField());
  }

  protected onTimeSet(time: string): void {
    this.form.get('sendTime')?.setValue(time);
  }

  protected onDatepickerClose(): void {
    if (this.form.get('sendDate')?.value) this.timepicker()?.open();
  }
}
